import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { adSpacesRadios } from '@/mocks/adSpaces';
import type { RadioAdSpaces, AdSpace } from './types';

type LoadingState = 'loading' | 'ready' | 'error';

const SPACE_LABELS: Record<string, string> = {
  'banner-principal': 'Banner Ppal.',
  'banner-secundario': 'Banner Sec.',
  'patrocinador-principal': 'Patroc. Pp al.',
  'patrocinador-evento': 'Patroc. Evento',
  'patrocinador-concurso': 'Patroc. Concurso',
  'publicacion-patrocinada': 'Pub. Patroc.',
  'programa-patrocinado': 'Prog. Patroc.',
  'locutor-patrocinado': 'Locutor Patroc.',
};

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 500 + Math.random() * 350;
    setTimeout(() => {
      if (Math.random() > 0.96) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

/* ── KPI Card ── */
function KpiCard({
  label, value, icon, color, bg, subtitle,
}: { label: string; value: string; icon: string; color: string; bg: string; subtitle?: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${color} text-lg`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-400 text-[10px] font-medium truncate">{label}</p>
        <p className="text-foreground-800 text-lg font-bold leading-tight">{value}</p>
        {subtitle && <p className="text-foreground-400 text-[9px] mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

/* ── Toggle Pill (Sí / No) ── */
function TogglePill({
  available, spaceName, radioName, onToggle,
}: { available: boolean; spaceName: string; radioName: string; onToggle: () => void }) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold cursor-pointer transition-all whitespace-nowrap ${
        available
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
          : 'bg-background-200 text-foreground-400 border border-background-300 hover:bg-background-300'
      }`}
      title={`${radioName} · ${spaceName}: ${available ? 'Disponible para RFW' : 'No disponible'}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${available ? 'bg-emerald-500' : 'bg-foreground-300'}`}></span>
      {available ? 'Sí' : 'No'}
    </button>
  );
}

/* ── Space Icon Header ── */
function SpaceHeader({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1 text-center" title={label}>
      <div className="w-7 h-7 rounded-lg bg-background-200 flex items-center justify-center">
        <i className={`${icon} text-foreground-400 text-xs`}></i>
      </div>
      <span className="text-foreground-400 text-[9px] font-semibold leading-tight max-w-[72px]">{label}</span>
    </div>
  );
}

/* ── Summary Badge ── */
function SummaryBadge({ enabled, total }: { enabled: number; total: number }) {
  const pct = total > 0 ? Math.round((enabled / total) * 100) : 0;
  const colorClass = pct >= 75 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : pct >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-600 border-red-200';

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${colorClass}`}>
      {enabled}/{total}
      <span className="opacity-60">· {pct}%</span>
    </span>
  );
}

/* ── Radio Row (Desktop) ── */
function RadioRow({
  radio, onToggleSpace,
}: { radio: RadioAdSpaces; onToggleSpace: (radioId: number, spaceId: string, current: boolean, spaceName: string) => void }) {
  const planBg = radio.plan === 'Premium' ? 'bg-violet-50 text-violet-700' : radio.plan === 'Estándar' ? 'bg-sky-50 text-sky-700' : 'bg-background-200 text-foreground-500';
  const enabledCount = radio.spaces.filter((s) => s.available).length;

  return (
    <tr className="border-b border-background-200 hover:bg-background-50/50 transition-colors">
      {/* Radio info */}
      <td className="py-3 px-3 sticky left-0 bg-background-50 z-[2]">
        <div className="flex items-center gap-2.5 min-w-[160px]">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${enabledCount > 0 ? 'bg-accent-50' : 'bg-background-200'}`}>
            <i className={`ri-radio-line ${enabledCount > 0 ? 'text-accent-500' : 'text-foreground-300'} text-sm`}></i>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-800 text-xs font-semibold truncate">{radio.radioName}</p>
            <p className="text-foreground-400 text-[9px]">{radio.radioFrequency} · {radio.radioCity}</p>
          </div>
          <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[8px] font-medium ${planBg} shrink-0`}>
            {radio.plan}
          </span>
        </div>
      </td>
      {/* Space toggles */}
      {radio.spaces.map((space) => (
        <td key={space.id} className="py-3 px-1.5 text-center">
          <TogglePill
            available={space.available}
            spaceName={space.name}
            radioName={radio.radioName}
            onToggle={() => onToggleSpace(radio.id, space.id, space.available, space.name)}
          />
        </td>
      ))}
      {/* Summary */}
      <td className="py-3 px-2 text-center">
        <SummaryBadge enabled={enabledCount} total={radio.spaces.length} />
      </td>
    </tr>
  );
}

/* ── Mobile Radio Card ── */
function RadioMobileCard({
  radio, onToggleSpace,
}: { radio: RadioAdSpaces; onToggleSpace: (radioId: number, spaceId: string, current: boolean, spaceName: string) => void }) {
  const planBg = radio.plan === 'Premium' ? 'bg-violet-50 text-violet-700' : radio.plan === 'Estándar' ? 'bg-sky-50 text-sky-700' : 'bg-background-200 text-foreground-500';
  const enabledCount = radio.spaces.filter((s) => s.available).length;

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${enabledCount > 0 ? 'bg-accent-50' : 'bg-background-200'}`}>
            <i className={`ri-radio-line ${enabledCount > 0 ? 'text-accent-500' : 'text-foreground-300'} text-sm`}></i>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-800 text-xs font-semibold truncate">{radio.radioName}</p>
            <p className="text-foreground-400 text-[9px]">{radio.radioFrequency} · {radio.radioCity}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[8px] font-medium ${planBg}`}>
            {radio.plan}
          </span>
          <SummaryBadge enabled={enabledCount} total={radio.spaces.length} />
        </div>
      </div>

      {/* Spaces grid */}
      <div className="grid grid-cols-2 gap-1.5">
        {radio.spaces.map((space) => (
          <div key={space.id} className="flex items-center justify-between bg-background-100 rounded-lg px-2.5 py-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <i className={`${space.icon} text-foreground-400 text-[10px] shrink-0`}></i>
              <span className="text-foreground-600 text-[10px] font-medium truncate">{space.name}</span>
            </div>
            <TogglePill
              available={space.available}
              spaceName={space.name}
              radioName={radio.radioName}
              onToggle={() => onToggleSpace(radio.id, space.id, space.available, space.name)}
            />
          </div>
        ))}
      </div>

      {radio.lastUpdated !== '—' && (
        <p className="text-foreground-300 text-[9px] text-right">Actualizado: {radio.lastUpdated}</p>
      )}
    </div>
  );
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
            <div className="w-10 h-10 rounded-xl bg-background-200"></div>
            <div className="space-y-1">
              <div className="w-16 h-2.5 bg-background-200 rounded"></div>
              <div className="w-10 h-4 bg-background-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-14 bg-background-200 rounded-xl animate-pulse"></div>
        ))}
      </div>
    </div>
  );
}

/* ── Error State ── */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
        <i className="ri-error-warning-line text-red-400 text-2xl"></i>
      </div>
      <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
      <p className="text-foreground-400 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line text-sm"></i>
        Reintentar
      </button>
    </div>
  );
}

/* ── Empty State ── */
function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
        <i className={`${icon} text-foreground-300 text-xl`}></i>
      </div>
      <p className="text-foreground-500 text-sm font-medium">{title}</p>
      <p className="text-foreground-300 text-xs mt-1">{subtitle}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */

export default function SAAdSpaces() {
  const [radios, setRadios] = useState<RadioAdSpaces[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [inlineMsg, setInlineMsg] = useState<string | null>(null);

  const showInline = useCallback((msg: string) => {
    setInlineMsg(msg);
    setTimeout(() => setInlineMsg(null), 3500);
  }, []);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad(adSpacesRadios)
      .then((data) => {
        setRadios(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ── Toggle a space ── */
  const handleToggleSpace = useCallback((radioId: number, spaceId: string, current: boolean, spaceName: string) => {
    const radio = radios.find((r) => r.id === radioId);
    if (!radio) return;

    const action = current ? 'desmarcar' : 'marcar';
    const title = current
      ? `¿Quitar "${spaceName}" de la Red Publicitaria?`
      : `¿Activar "${spaceName}" para la Red Publicitaria?`;
    const text = current
      ? `"${radio.radioName}" dejará de ofrecer "${spaceName}" a RFW. Los anunciantes ya no podrán usar este espacio.`
      : `"${radio.radioName}" ofrecerá "${spaceName}" a la Red Publicitaria RFW. RFW podrá colocar anuncios automáticamente en este espacio.`;

    Swal.fire({
      icon: 'question',
      title,
      text,
      showCancelButton: true,
      confirmButtonText: current ? 'Sí, quitar' : 'Sí, activar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: current ? '#ef4444' : '#1a1a1a',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setRadios((prev) =>
          prev.map((r) => {
            if (r.id !== radioId) return r;
            return {
              ...r,
              spaces: r.spaces.map((s) =>
                s.id === spaceId ? { ...s, available: !s.available } : s,
              ),
              lastUpdated: new Date().toISOString().slice(0, 10),
            };
          }),
        );
        const msg = current
          ? `"${spaceName}" desactivado para ${radio.radioName}`
          : `"${spaceName}" ahora disponible en RFW para ${radio.radioName}`;
        showInline(msg);
      }
    });
  }, [radios, showInline]);

  /* ── Computed metrics ── */
  const computedMetrics = useMemo(() => {
    const totalRadios = radios.length;
    const totalSpaces = radios.reduce((s, r) => s + r.spaces.length, 0);
    const enabledSpaces = radios.reduce((s, r) => s + r.spaces.filter((sp) => sp.available).length, 0);
    const coveragePct = totalSpaces > 0 ? ((enabledSpaces / totalSpaces) * 100).toFixed(0) : '0';
    const radiosWithSpaces = radios.filter((r) => r.spaces.some((sp) => sp.available)).length;

    /* Per-space totals */
    const spaceTotals = radios[0]?.spaces.map((sp) => {
      const totalAvailable = radios.filter((r) => {
        const found = r.spaces.find((s) => s.id === sp.id);
        return found?.available;
      }).length;
      return { id: sp.id, name: sp.name, totalAvailable };
    }) || [];

    return { totalRadios, totalSpaces, enabledSpaces, coveragePct, radiosWithSpaces, spaceTotals };
  }, [radios]);

  /* ── Filtered radios ── */
  const filteredRadios = useMemo(() => {
    if (!searchTerm.trim()) return radios;
    const q = searchTerm.toLowerCase();
    return radios.filter(
      (r) =>
        r.radioName.toLowerCase().includes(q) ||
        r.radioFrequency.toLowerCase().includes(q) ||
        r.radioCity.toLowerCase().includes(q) ||
        r.plan.toLowerCase().includes(q),
    );
  }, [radios, searchTerm]);

  /* Space columns definition from first radio */
  const spaceColumns = radios.length > 0 ? radios[0].spaces : [];

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Espacios Publicitarios</span>
            </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold mb-2">
                  <i className="ri-layout-grid-line text-xs"></i>
                  Bloque SA-42
                </div>
                <h2 className="text-foreground-950 font-bold text-lg">Espacios Publicitarios Radio</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Inventario de espacios · Cada radio define qué espacios ofrece a RFW
                  {loadingState === 'ready' && ` · ${computedMetrics.enabledSpaces} de ${computedMetrics.totalSpaces} espacios activados (${computedMetrics.coveragePct}%)`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-full sm:w-56">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar radio, ciudad..."
                    disabled={loadingState !== 'ready'}
                    className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                    >
                      <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Inline message */}
            {inlineMsg && (
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-[10px] font-medium w-fit">
                <i className="ri-check-line text-emerald-500 text-xs"></i>
                {inlineMsg}
              </div>
            )}

            {/* KPIs */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <KpiCard
                  label="Radios Totales"
                  value={String(computedMetrics.totalRadios)}
                  icon="ri-radio-line"
                  color="text-foreground-700"
                  bg="bg-background-200"
                />
                <KpiCard
                  label="Radios con Espacios"
                  value={String(computedMetrics.radiosWithSpaces)}
                  icon="ri-check-double-line"
                  color="text-accent-500"
                  bg="bg-accent-50"
                  subtitle={`de ${computedMetrics.totalRadios} radios`}
                />
                <KpiCard
                  label="Espacios Activados"
                  value={`${computedMetrics.enabledSpaces} / ${computedMetrics.totalSpaces}`}
                  icon="ri-grid-line"
                  color="text-emerald-500"
                  bg="bg-emerald-50"
                  subtitle={`${computedMetrics.coveragePct}% de cobertura`}
                />
                <KpiCard
                  label="Tipos de Espacio"
                  value="8"
                  icon="ri-stack-line"
                  color="text-amber-500"
                  bg="bg-amber-50"
                  subtitle="Banners, patrocinios, publicaciones"
                />
              </div>
            )}

            {/* Loading KPIs */}
            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-xl bg-background-200"></div>
                    <div className="space-y-1">
                      <div className="w-16 h-2.5 bg-background-200 rounded"></div>
                      <div className="w-10 h-4 bg-background-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Table / Cards */}
            {loadingState === 'loading' && <LoadingSkeleton />}

            {loadingState === 'error' && <ErrorState message={errorMsg || 'Error'} onRetry={loadData} />}

            {loadingState === 'ready' && filteredRadios.length === 0 && (
              <EmptyState icon="ri-radio-line" title="Sin resultados" subtitle="No hay radios que coincidan con tu búsqueda" />
            )}

            {loadingState === 'ready' && filteredRadios.length > 0 && (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1100px]">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-100/50">
                          <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider sticky left-0 bg-background-100/50 z-[2]">
                            Radio
                          </th>
                          {spaceColumns.map((sp) => (
                            <th key={sp.id} className="text-center py-3 px-1.5 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">
                              <SpaceHeader icon={sp.icon} label={SPACE_LABELS[sp.id] || sp.name} />
                            </th>
                          ))}
                          <th className="text-center py-3 px-2 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider w-[90px]">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRadios.map((radio) => (
                          <RadioRow key={radio.id} radio={radio} onToggleSpace={handleToggleSpace} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-2.5">
                  {filteredRadios.map((radio) => (
                    <RadioMobileCard key={radio.id} radio={radio} onToggleSpace={handleToggleSpace} />
                  ))}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Espacios Publicitarios Radio
        </p>
      </div>
    </div>
  );
}